import { Box, Typography, useTheme, CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
import { tokens } from '../../theme';
import axios from 'axios';

const TokenPriceBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [tokenData, setTokenData] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch token prices and OHLCV data
  const fetchTokenPrices = async () => {
    try {
      const results = {};

      // 1. First get HIVE price in USD from CoinGecko
      let hiveUsdPrice = 0;
      try {
        const hiveResponse = await axios.get(
          'https://api.coingecko.com/api/v3/simple/price?ids=hive&vs_currencies=usd&include_24hr_change=true'
        );
        
        if (hiveResponse.data && hiveResponse.data.hive) {
          hiveUsdPrice = hiveResponse.data.hive.usd;
          results.HIVE = {
            price: hiveUsdPrice,
            change24h: hiveResponse.data.hive.usd_24h_change || 0,
            chartData: [],
            isPositive: (hiveResponse.data.hive.usd_24h_change || 0) >= 0
          };
        }
      } catch (err) {
        console.error('Error fetching HIVE price:', err);
      }

      // 2. Fetch SPS and DEC from TribalDex (prices are in HIVE)
      const tribalTokens = ['SPS', 'DEC'];
      await Promise.all(
        tribalTokens.map(async (symbol) => {
          try {
            // Get OHLCV data from TribalDex (price in HIVE)
            const ohlcvResponse = await axios.get(
              `https://info-api.tribaldex.com/market/ohlcv?symbol=${symbol}&interval=daily`
            );
            
            if (ohlcvResponse.data && ohlcvResponse.data.length >= 2 && hiveUsdPrice > 0) {
              const today = ohlcvResponse.data[ohlcvResponse.data.length - 1];
              const yesterday = ohlcvResponse.data[ohlcvResponse.data.length - 2];
              
              // Convert HIVE price to USD
              const currentPriceHive = parseFloat(today.close);
              const previousPriceHive = parseFloat(yesterday.close);
              const currentPrice = currentPriceHive * hiveUsdPrice;
              const previousPrice = previousPriceHive * hiveUsdPrice;
              const change24h = ((currentPrice - previousPrice) / previousPrice) * 100;
              
              // Get last 7 data points for mini chart (convert to USD)
              const chartData = ohlcvResponse.data
                .slice(-7)
                .map(d => parseFloat(d.close) * hiveUsdPrice);
              
              results[symbol] = {
                price: currentPrice,
                change24h,
                chartData,
                isPositive: change24h >= 0
              };
            }
          } catch (err) {
            console.error(`Error fetching ${symbol}:`, err);
          }
        })
      );

      // 3. Fetch BTC price from CoinGecko
      try {
        const btcResponse = await axios.get(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd&include_24hr_change=true'
        );
        
        if (btcResponse.data && btcResponse.data.bitcoin) {
          results.BTC = {
            price: btcResponse.data.bitcoin.usd,
            change24h: btcResponse.data.bitcoin.usd_24h_change || 0,
            chartData: [], // No mini chart for BTC
            isPositive: (btcResponse.data.bitcoin.usd_24h_change || 0) >= 0
          };
        }
      } catch (err) {
        console.error('Error fetching BTC:', err);
      }

      setTokenData(results);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching token prices:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokenPrices();
    
    // Refresh every 2 minutes
    const interval = setInterval(fetchTokenPrices, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Format price based on magnitude
  const formatPrice = (price, symbol) => {
    if (symbol === 'BTC') {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    if (price < 0.01) {
      return `$${price.toFixed(6)}`;
    }
    if (price < 1) {
      return `$${price.toFixed(4)}`;
    }
    return `$${price.toFixed(2)}`;
  };

  // Mini sparkline chart
  const MiniChart = ({ data, isPositive }) => {
    if (!data || data.length === 0) return null;

    const width = 40;
    const height = 20;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    const points = data
      .map((value, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - ((value - min) / range) * height;
        return `${x},${y}`;
      })
      .join(' ');

    return (
      <svg width={width} height={height} style={{ display: 'block' }}>
        <polyline
          points={points}
          fill="none"
          stroke={isPositive ? '#4caf50' : '#f44336'}
          strokeWidth="1.5"
        />
      </svg>
    );
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box 
      display="flex" 
      alignItems="center" 
      gap={2}
      sx={{
        backgroundColor: 'transparent',
        px: 1
      }}
    >
      {['SPS', 'DEC', 'HIVE', 'BTC'].map((symbol, index) => {
        const data = tokenData[symbol];
        if (!data) return null;

        const changeColor = data.isPositive ? '#4caf50' : '#f44336';

        return (
          <Box key={symbol} display="flex" alignItems="center">
            <Box
              display="flex"
              alignItems="center"
              gap={0.8}
              sx={{
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Symbol */}
              <Typography
                variant="caption"
                sx={{
                  color: colors.grey[100],
                  fontWeight: 700,
                  fontSize: '0.85rem'
                }}
              >
                {symbol}
              </Typography>

              {/* Price */}
              <Typography
                variant="caption"
                sx={{
                  color: colors.grey[100],
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              >
                {formatPrice(data.price, symbol)}
              </Typography>

              {/* 24h Change */}
              <Box display="flex" alignItems="center" gap={0.3}>
              
                <Typography
                  variant="caption"
                  sx={{
                    color: changeColor,
                    fontWeight: 700,
                    fontSize: '0.75rem'
                  }}
                >
                  {data.change24h >= 0 ? '+' : ''}{data.change24h.toFixed(2)}%
                </Typography>
              </Box>
            </Box>
            
            {/* Divider - nicht nach dem letzten Element */}
            {index < 3 && (
              <Box
                sx={{
                  width: '1px',
                  height: '24px',
                  backgroundColor: colors.grey[700],
                  mx: 2,
                  opacity: 0.5
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default TokenPriceBar;
